import { unwrap } from "@lvt/web-data";
import { Grid, IconButton, Box } from "@material-ui/core";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import { skipToken } from "@reduxjs/toolkit/query/react";
import clsx from "clsx";
import { useRef } from "react";
import { Route, useHistory } from "react-router";
import { Switch } from "react-router-dom";

import { useGetLiveUnitDefaultStreamCameraIdQuery } from "@/api/liveUnits/defaultCamera";
import { LiveUnitNavSearchBarStyles, SearchBarStyles } from "@/common/components/LiveUnitNav/LiveUnitNavSearchBarStyles";
import { useLiveUnitNavStyles } from "@/common/components/LiveUnitNav/LiveUnitNavStyles";
import { VmsSearch } from "@/common/components/VmsSearch";
import { useSelector } from "@/store";
import * as FullStory from "@/utilities/hooks/fullstory";
import { getRecentCameras } from "@/utilities/localStorage/recentStreamingCameras";
import { PageType } from "@/utilities/types/common";
import { UnitType } from "@/utilities/types/liveunit";

import { Header } from "../Header";
import { LiveUnitNavTabs, NavTabs } from "./LiveUnitNavTabs";


export const searchPlaceholder = "Search Live Units";

const LiveUnitNav: React.FC = () => {
    const hidden = useSelector((state) => state.liveUnitNav.hidden);
    const streamMeta = useSelector((state) => unwrap(state.stream.streamMeta));
    const streamingLiveUnitId = streamMeta?.liveUnitId;
    const archiveLiveUnitId = useSelector((state) => state.archives.liveUnitId);
    const ref = useRef<HTMLDivElement>(null);
    const history = useHistory();
    const { currentData: archiveDefaultCameraId } = useGetLiveUnitDefaultStreamCameraIdQuery(archiveLiveUnitId ?? skipToken);

    const recentCameras = getRecentCameras();
    const classes = useLiveUnitNavStyles();
    const mapSearchBarClasses = LiveUnitNavSearchBarStyles();
    const searchBarClasses = SearchBarStyles();

    const setStreamUrl = (streamId: number) => `/camera/${streamId}/stream`;
    const setHistoryUrl = (liveUnitId: number) => `/live-unit/${liveUnitId}/history`;

    const backToMap = () => {
        // TODO: history.goBack();
        history.push("/");
    };

    return (
        <div ref={ref} className={clsx(classes.root, hidden && classes.hidden)}>
            <Grid container className={classes.inner} justifyContent="flex-start">
                <Grid item sm={12} lg={12}>
                    <Switch>
                        <Route
                            path="/camera/:cameraId/stream"
                            render={({ match }) => {
                                const historyUrl = streamingLiveUnitId ? setHistoryUrl(streamingLiveUnitId) : null;

                                return (
                                    <div className={classes.navWrapper}>
                                        <div className={classes.titleWrapper}>
                                            <IconButton className={clsx(FullStory.names.classes.backButton.common, FullStory.names.classes.backButton.stream, classes.back)} onClick={backToMap}><KeyboardBackspaceIcon /></IconButton>
                                            {streamingLiveUnitId && <Header unitType={UnitType.LiveUnit} pageType={PageType.Stream} unitId={streamingLiveUnitId} />}
                                        </div>
                                        <LiveUnitNavTabs
                                            value={NavTabs.Stream}
                                            streamUrl={match.url}
                                            historyUrl={historyUrl}
                                        />
                                        <Box className={classes.placeholderSearchBox}></Box>
                                        <VmsSearch
                                            customStyle={searchBarClasses}
                                            pageType={PageType.Stream}
                                        />
                                    </div>
                                );
                            }}
                        />
                        <Route
                            path="/live-unit/:liveUnitId/history"
                            render={({ match }) => {
                                const recentStreamId = archiveLiveUnitId ? recentCameras.get(archiveLiveUnitId) : null;
                                const streamId = recentStreamId ? recentStreamId : archiveDefaultCameraId;
                                const streamUrl = streamId ? setStreamUrl(streamId) : null;

                                return (
                                    <div className={classes.navWrapper}>
                                        <div className={classes.titleWrapper}>
                                            <IconButton className={clsx(FullStory.names.classes.backButton.common, FullStory.names.classes.backButton.history)} onClick={backToMap}><KeyboardBackspaceIcon /></IconButton>
                                            <Header unitType={UnitType.LiveUnit} pageType={PageType.History} unitId={parseInt(match.params.liveUnitId, 10)} />
                                        </div>
                                        <LiveUnitNavTabs
                                            value={NavTabs.History}
                                            streamUrl={streamUrl}
                                            historyUrl={match.url}
                                        />
                                        <Box className={classes.placeholderSearchBox}></Box>
                                        <VmsSearch
                                            customStyle={searchBarClasses}
                                            pageType={PageType.History}
                                        />
                                    </div>
                                );
                            }}
                        />
                        <Route
                            path="/live-unit/:liveUnitId/requested-video"
                            render={({ match }) => {
                                const recentStreamId = archiveLiveUnitId ? recentCameras.get(archiveLiveUnitId) : null;
                                const streamId = recentStreamId ? recentStreamId : archiveDefaultCameraId;
                                const streamUrl = streamId ? setStreamUrl(streamId) : null;

                                return (
                                    <div className={classes.navWrapper}>
                                        <div className={classes.titleWrapper}>
                                            <IconButton className={clsx(FullStory.names.classes.backButton.common, FullStory.names.classes.backButton.history)} onClick={backToMap}><KeyboardBackspaceIcon /></IconButton>
                                            <Header unitType={UnitType.LiveUnit} pageType={PageType.History} unitId={parseInt(match.params.liveUnitId, 10)} />
                                        </div>
                                        <LiveUnitNavTabs
                                            value={NavTabs.History}
                                            streamUrl={streamUrl}
                                            historyUrl={"history"}
                                            onRequestedVideo
                                        />
                                        <Box className={classes.placeholderSearchBox}></Box>
                                        <VmsSearch
                                            customStyle={searchBarClasses}
                                            pageType={PageType.History}
                                        />
                                    </div>
                                );
                            }}
                        />
                        <Route
                            path="/archive-cabinet/:cabinetId/history"
                            render={({ match }) => {
                                return (
                                    <div className={classes.navWrapper}>
                                        <div className={classes.titleWrapper}>
                                            <IconButton onClick={backToMap}><KeyboardBackspaceIcon /></IconButton>
                                            <Header unitType={UnitType.ArchiveCabinet} pageType={PageType.History} unitId={parseInt(match.params.cabinetId, 10)} />
                                        </div>
                                        <LiveUnitNavTabs
                                            value={NavTabs.History}
                                            streamUrl={null}
                                            historyUrl={match.url}
                                        />
                                        <Box className={classes.placeholderSearchBox}></Box>
                                        <VmsSearch
                                            customStyle={searchBarClasses}
                                            pageType={PageType.History}
                                        />
                                    </div>
                                );
                            }}
                        />
                        <Route
                            path="/"
                            render={() => {
                                return <VmsSearch customStyle={mapSearchBarClasses} pageType={PageType.Map} />;
                            }}
                        />
                    </Switch>
                </Grid>
            </Grid>
        </div>
    );
};

export default LiveUnitNav;
